import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Modal from '../../components/shared/Modal';
import { useIsMobile } from '../../utils/useIsMobile';
import { fileToCompressedDataUrl } from '../../utils/image';

const MAX_IMAGENES = 3;

// Valor del desplegable para un ticket de la plataforma, sin negocio asociado.
const INTERNO = '__interno__';
const ETIQUETA_INTERNO = 'Plataforma (interno)';

const ESTADOS = {
  en_proceso: { label: 'En proceso', color: 'info' },
  atendido:   { label: 'Atendido',   color: 'warning' },
  cerrado:    { label: 'Cerrado',    color: 'success' },
};

const fmtFecha = (f) => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? f : d.toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
};

export default function TicketsPage() {
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('');
  const [selected, setSelected] = useState(null);
  const [estado, setEstado]     = useState('en_proceso');
  const [detalle, setDetalle]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState('');
  const [error, setError]       = useState('');
  const [imagenes, setImagenes] = useState([]);
  const [ampliada, setAmpliada] = useState(null);
  const esMovil = useIsMobile();

  // ── Alta de tickets desde el propio panel de soporte ──────────────────────
  const [negocios, setNegocios]   = useState([]);
  const [nuevo, setNuevo]         = useState(false);
  const [nNegocio, setNNegocio]   = useState('');
  const [nDescrip, setNDescrip]   = useState('');
  const [nImagenes, setNImagenes] = useState([]);
  const [creando, setCreando]     = useState(false);

  useEffect(() => {
    api.get('/businesses').then(r => setNegocios(r.data || [])).catch(() => setNegocios([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tickets', { params: { status: filter || undefined } });
      setTickets(res.data);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const notify = (msg, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 4000); }
    else          { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }
  };

  const abrirNuevo = () => {
    setNNegocio(''); setNDescrip(''); setNImagenes([]);
    setNuevo(true);
  };

  const agregarImagenes = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!files.length) return;
    if (nImagenes.length + files.length > MAX_IMAGENES) {
      return notify(`Máximo ${MAX_IMAGENES} imágenes por ticket`, true);
    }
    try {
      const nuevas = await Promise.all(files.map(f => fileToCompressedDataUrl(f)));
      setNImagenes(prev => [...prev, ...nuevas]);
    } catch (err) {
      notify(err.message || 'No se pudo procesar la imagen', true);
    }
  };

  const quitarImagen = (idx) => setNImagenes(prev => prev.filter((_, i) => i !== idx));

  const crearTicket = async () => {
    if (!nNegocio)         return notify('Elige el negocio o marca el ticket como interno', true);
    if (!nDescrip.trim())  return notify('Escribe la descripción de la novedad', true);
    const esInterno = nNegocio === INTERNO;
    setCreando(true);
    try {
      await api.post('/tickets', {
        negocio_id: esInterno ? null : nNegocio,
        interno: esInterno,
        descripcion: nDescrip,
        imagenes: nImagenes,
      });
      setNuevo(false);
      notify('Ticket creado');
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Error creando el ticket', true);
    } finally { setCreando(false); }
  };

  const open = async (t) => {
    setSelected(t);
    setEstado(t.estado || 'en_proceso');
    setDetalle(t.detalle_solucion || '');
    setImagenes([]);
    if (t.tiene_imagenes) {
      try {
        const res = await api.get(`/tickets/${t.id}/images`);
        setImagenes(res.data.imagenes || []);
      } catch { /* sin imágenes si falla la carga */ }
    }
  };

  const guardar = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/tickets/${selected.id}`, { estado, detalle_solucion: detalle });
      notify('Ticket actualizado');
      setSelected(null);
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Error guardando el ticket', true);
    } finally { setSaving(false); }
  };

  // En celular los datos se apilan; en escritorio van etiqueta / valor en linea
  const infoRow = (label, value) => (
    <div style={{
      display: 'flex',
      flexDirection: esMovil ? 'column' : 'row',
      justifyContent: 'space-between',
      gap: esMovil ? 2 : 12,
      padding: '7px 0', borderBottom: '1px solid var(--color-border)', fontSize: '0.85rem',
    }}>
      <span style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ textAlign: esMovil ? 'left' : 'right', fontWeight: 500, wordBreak: 'break-word' }}>
        {value || '—'}
      </span>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Soporte</h1>
          <p className="page-subtitle">Tickets de soporte de los negocios</p>
        </div>
        <button className="btn btn-primary" onClick={abrirNuevo}>+ Nuevo ticket</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}
      {error   && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {[['', 'Todos'], ['en_proceso', 'En proceso'], ['atendido', 'Atendidos'], ['cerrado', 'Cerrados']].map(([v, l]) => (
          <button key={v} className={`btn ${filter === v ? 'btn-primary' : 'btn-ghost'}`} style={{ fontSize: '0.85rem' }} onClick={() => setFilter(v)}>{l}</button>
        ))}
      </div>

      {/* Lista. El detalle se abre en un modal, no en una columna al lado. */}
      <div>
        <div className="card" style={{ minWidth: 0 }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : tickets.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>No hay tickets</div>
          ) : (
            tickets.map(t => {
              const est = ESTADOS[t.estado] || { label: t.estado, color: 'secondary' };
              return (
                <div
                  key={t.id}
                  onClick={() => open(t)}
                  style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    background: selected?.id === t.id ? 'rgba(79,70,229,0.08)' : 'transparent',
                    borderLeft: selected?.id === t.id ? '3px solid var(--color-gold)' : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 8 }}>
                    <div style={{
                      fontWeight: 600, fontSize: '0.9rem', minWidth: 0, wordBreak: 'break-word',
                      // Los internos se distinguen a simple vista de los de negocios
                      color: t.business_name ? undefined : 'var(--color-text-muted)',
                      fontStyle: t.business_name ? undefined : 'italic',
                    }}>
                      {t.business_name || ETIQUETA_INTERNO}
                    </div>
                    <span className={`badge badge-${est.color}`} style={{ fontSize: '0.65rem', flexShrink: 0 }}>{est.label}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.descripcion}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    {t.user_name} · {fmtFecha(t.creado_en)}
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>

      {/* Detalle */}
      {selected && (
        <Modal
          titulo={`Ticket #${selected.id}`}
          ancho={620}
          onCerrar={() => setSelected(null)}
          pie={<>
            <button className="btn btn-ghost" onClick={() => setSelected(null)}>Cerrar</button>
            <button className="btn btn-primary" onClick={guardar} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </>}
        >
          <div>
              <div style={{ marginBottom: '1.25rem' }}>
                {infoRow('Reportado por', selected.user_name)}
                {infoRow('Negocio', selected.business_name || ETIQUETA_INTERNO)}
                {/* Un ticket interno no tiene negocio, asi que tampoco administrador */}
                {selected.business_name && infoRow('Administrador del negocio', selected.admin_name)}
                {infoRow('Fecha del ticket', fmtFecha(selected.creado_en))}
              </div>

              <div className="form-group">
                <label className="form-label">Descripción de la novedad</label>
                <div style={{ background: 'var(--color-surface)', borderRadius: 8, padding: '1rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
                  {selected.descripcion}
                </div>
              </div>

              {imagenes.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Imágenes adjuntas</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {imagenes.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Adjunto ${i + 1}`}
                        onClick={() => setAmpliada(img)}
                        style={{
                          width: 90, height: 90, objectFit: 'cover', borderRadius: 8,
                          border: '1px solid var(--color-border)', cursor: 'zoom-in',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={estado} onChange={e => setEstado(e.target.value)}>
                  <option value="en_proceso">En proceso</option>
                  <option value="atendido">Atendido</option>
                  <option value="cerrado">Cerrado</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Detalle de la solución</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={detalle}
                  onChange={e => setDetalle(e.target.value)}
                  placeholder="Describe cómo se resolvió la novedad..."
                />
              </div>

          </div>
        </Modal>
      )}

      {/* Nuevo ticket */}
      {nuevo && (
        <Modal
          titulo="Nuevo ticket"
          onCerrar={() => !creando && setNuevo(false)}
          pie={<>
            <button className="btn btn-ghost" onClick={() => setNuevo(false)} disabled={creando}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={crearTicket} disabled={creando}>
              {creando ? 'Creando...' : 'Crear ticket'}
            </button>
          </>}
        >
          <div>
              {/* El aviso de la pagina queda detras del modal, asi que se repite aqui */}
              {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

              <div className="form-group">
                <label className="form-label">Negocio</label>
                <select className="form-select" value={nNegocio} onChange={e => setNNegocio(e.target.value)}>
                  <option value="">Selecciona una opción...</option>
                  <option value={INTERNO}>Interno — panel de super administrador</option>
                  <optgroup label="Negocios">
                    {negocios.map(n => (
                      <option key={n.id} value={n.id}>{n.nombre || n.name}</option>
                    ))}
                  </optgroup>
                </select>
                {nNegocio === INTERNO && (
                  <p style={{ margin: '6px 2px 0', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    El ticket no se asociará a ningún negocio: queda como novedad de la plataforma.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Descripción de la novedad</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={nDescrip}
                  onChange={e => setNDescrip(e.target.value)}
                  placeholder="Describe el problema o la novedad que quieres registrar..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Imágenes (opcional, máx. {MAX_IMAGENES})</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  {nImagenes.map((img, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img
                        src={img}
                        alt={`Adjunto ${i + 1}`}
                        onClick={() => setAmpliada(img)}
                        style={{
                          width: 84, height: 84, objectFit: 'cover', borderRadius: 8,
                          border: '1px solid var(--color-border)', cursor: 'zoom-in',
                        }}
                      />
                      <button
                        onClick={() => quitarImagen(i)}
                        title="Quitar imagen"
                        style={{
                          position: 'absolute', top: -6, right: -6, width: 22, height: 22,
                          borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff',
                          cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1,
                        }}
                      >✕</button>
                    </div>
                  ))}
                  {nImagenes.length < MAX_IMAGENES && (
                    <label style={{
                      width: 84, height: 84, borderRadius: 8, border: '2px dashed var(--color-border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                      cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.7rem', gap: 2,
                    }}>
                      <span style={{ fontSize: '1.3rem' }}>📷</span>
                      Adjuntar
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={agregarImagenes} />
                    </label>
                  )}
                </div>
              </div>

          </div>
        </Modal>
      )}

      {/* Visor de imagen ampliada */}
      {ampliada && (
        <div
          onClick={() => setAmpliada(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: '2rem',
          }}
        >
          <img src={ampliada} alt="Imagen ampliada" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
